import React, { useCallback, useEffect, useState, useRef } from "react";
import { useLocation } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { get, isEmpty } from "lodash";
import ApolloContext from "@tvg/utils/apolloContext";
import mediator from "@tvg/mediator";
// Types
import ApolloContextClients from "@tvg/utils/types/ApolloContext";
import { BetBasicInfo, BetRepeatInfo } from "@tvg/ts-types/Bet";
import { RaceProgram, RaceTypeCodeEnum } from "@tvg/ts-types/Race";
// Utils
import BetUtils from "@tvg/utils/betSelection";
import { AlertInline, breakpoints, useMediaQuery } from "@tvg/design-system";
import { onOpenComplianceModal } from "@tvg/sh-lib-account-wallet/src/utils/mobileUtils";
import { openErrorModal } from "@tvg/login-controller/src/actions/modal";
import { MediatorEventType } from "@tvg/amplitude/modules/programPage/types";
// Store
import {
  resetBetting,
  setSelectionScratched
} from "@tvg/desktop-bet/src/store/actions";
import {
  getBetSelections,
  getBetType
} from "@tvg/desktop-bet/src/store/selectors";
import { isAccountCompliantSelector } from "@tvg/sh-utils/sessionUtils";
import { getIsMyBetsOpen } from "@tvg/sh-lib-my-bets/redux/selectors";
import { downloadUrl, isTvg5 } from "@tvg/utils/generalUtils";
import { getBalance, getIsLogged } from "@urp/store-selectors";
import { isMobileServerSide } from "@tvg/sh-utils/mobileUtils";
import { DerbyBanner } from "@urp/derby-banner";
import { RaceVideoPlaylist } from "@urp/tips-picks";
import { getGeolocationError } from "@tvg/sh-geolocation/src/redux/selectors";
import { geoComplyErrorModal } from "@tvg/sh-geolocation/src/redux/actions";
import { formattedScrollableWagerType } from "../../utils/raceUtils";
import { isFdr, openLoginWall } from "../../utils/general";
import {
  getHasRequestedLogin,
  getProgramPageModuleConfigs,
  getIsUserVerified,
  getUserAcceptedTermsAndConditions,
  getIsPoolsNInfoModalOpen,
  getFppSectionContent,
  getEnableFppSection,
  getDerbyBannerImageToggle
} from "../../store/selectors";
import { setIsPoolsNInfoModalOpen } from "../../store/actions";
// Components
import CurrentRaceDetails from "./CurrentRaceDetails";
import RunnerList from "../RunnerList";
import BetSlip from "../BetSlip";
import RunnerModifier from "./RunnerModifier";
import ChangesCard from "./ChangesCard";
import WillPays from "./WillPays";
import {
  MainContainer,
  RaceCardContainer,
  MainWrapper,
  AuxiliaryModulesContainer,
  PrimaryModulesContainer,
  AlertInlineContainer,
  Divider
} from "./styled-components";
import ConfirmBetModal from "../ConfirmBetModal";
import BetsModal from "../BetsModal";
import RepeatBetModal from "../RepeatBetModal";
import RaceResultView from "./RaceResultView";
import RaceCardLoading from "../loadings/RaceCardLoading";
import MyBetsCard from "./MyBetsCard";
import Probables from "./Probables";
import PoolsCard from "./PoolsCard";
import { WAGER_TYPE_LIMIT } from "./PoolsCard/constants";
import PoolsNInfoModal from "../TrackHeader/PoolsNInfoModal";
// Hooks
import {
  useSetBetSelections,
  useScratchedRunners,
  useBetBlockedByState,
  useRunnerModifier,
  useFreePastPerformance,
  useIsLiveTv
} from "../../hooks";
import { TabSelectionEnum } from "../../types";
import PicksSelector from "../PicksSelector";
import FreePastPerformance from "./FreePastPerformance";
import useDerbyBanner from "../../hooks/useDerbyBanner";

interface MainProps {
  race: RaceProgram;
  races: RaceProgram[];
  isLoading?: boolean;
  nextRace: RaceProgram | undefined;
  isReplayModal?: boolean;
  onClickWatchReplay?: () => void;
}

const desktopMaxSizeBreakPoint = "(min-width: 1440px)";

export const Main = ({
  race,
  races,
  isLoading,
  nextRace,
  isReplayModal = false,
  onClickWatchReplay
}: MainProps) => {
  const isFDR = isFdr();
  const isLiveTv = useIsLiveTv();
  const isMobile =
    useMediaQuery(breakpoints.tablet.max.sm) || isMobileServerSide();
  const isDesktopMinXl = useMediaQuery(breakpoints.desktop.min.xl);
  const isDesktopMinXxl = useMediaQuery(breakpoints.desktop.min.xxl);
  const isDesktopMaxXl = useMediaQuery(breakpoints.desktop.max.xl);
  const isDesktopMaxXxl = useMediaQuery(breakpoints.desktop.max.xxl);
  const isDesktopMaxSize = useMediaQuery(desktopMaxSizeBreakPoint);

  const [isBetsModalOpen, setIsBetsModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isRepeatBetModalOpen, setIsRepeatBetModalOpen] = useState(false);
  const [requestBetWithAmount, setRequesteBetWithAmount] = useState<number>();
  const [selectedRaceWager, setSelectedRaceWager] = useState<
    BetBasicInfo | BetRepeatInfo | null
  >(null);

  const isPoolsNInfoModalOpen = useSelector(getIsPoolsNInfoModalOpen);
  const storedBetType = useSelector(getBetType);
  const isLogged = useSelector(getIsLogged);
  const userBalance = useSelector(getBalance);
  const hasRequestedLogin = useSelector(getHasRequestedLogin);
  const programPageModuleConfig = useSelector(getProgramPageModuleConfigs);
  const selections = useSelector(getBetSelections);
  const isUserVerified = useSelector(getIsUserVerified);
  const userAcceptedTermsAndConditions = useSelector(
    getUserAcceptedTermsAndConditions
  );
  const fppSectionContent = useSelector(getFppSectionContent);
  const enableFpp = useSelector(getEnableFppSection);
  const myBetsIsOpen = useSelector(getIsMyBetsOpen);
  const derbyBannerImageToggle = useSelector(getDerbyBannerImageToggle);
  const getGeolocationErrorSelector = useSelector(getGeolocationError);

  const raceCardContainerRef = useRef<HTMLDivElement>(null);
  const probablesRef = useRef<HTMLDivElement>(null);
  const myBetsContainerRef = useRef<HTMLDivElement>(null);
  const poolsContainerRef = useRef<HTMLDivElement>(null);
  const willPaysContainerRef = useRef<HTMLDivElement>(null);
  const changesContainerRef = useRef<HTMLDivElement>(null);

  const [primaryModulesHeight, setPrimaryModulesHeight] = useState<
    number | undefined
  >();
  const [auxiliaryModulesHeight, setAuxiliaryModulesHeight] = useState<
    number | undefined
  >();
  const [isAuxiliaryHigherThanPrimary, setIsAuxiliaryHigherThanPrimary] =
    useState<boolean | 0 | undefined>();
  const [myBetsMaxHeight, setMyBetsMaxHeight] = useState<number | undefined>();

  useEffect(() => {
    const raceCardContainerHeight =
      raceCardContainerRef.current?.getBoundingClientRect().height;

    const probablesContainerHeight =
      probablesRef.current?.getBoundingClientRect().height;

    const myBetsContainerHeight =
      myBetsContainerRef.current?.getBoundingClientRect().height;

    const poolsContainerHeight =
      poolsContainerRef.current?.getBoundingClientRect().height;

    const willPaysContainerHeight =
      willPaysContainerRef.current?.getBoundingClientRect().height;

    const changesContainerHeight =
      changesContainerRef.current?.getBoundingClientRect().height;

    setPrimaryModulesHeight(
      raceCardContainerHeight &&
        probablesContainerHeight &&
        raceCardContainerHeight + probablesContainerHeight
    );

    setAuxiliaryModulesHeight(
      myBetsContainerHeight &&
        poolsContainerHeight &&
        willPaysContainerHeight &&
        changesContainerHeight &&
        myBetsContainerHeight +
          poolsContainerHeight +
          willPaysContainerHeight +
          changesContainerHeight
    );

    setIsAuxiliaryHigherThanPrimary(
      primaryModulesHeight &&
        auxiliaryModulesHeight &&
        auxiliaryModulesHeight > primaryModulesHeight
    );

    setMyBetsMaxHeight(
      primaryModulesHeight &&
        poolsContainerHeight &&
        willPaysContainerHeight &&
        changesContainerHeight &&
        Math.floor(
          primaryModulesHeight -
            (poolsContainerHeight +
              willPaysContainerHeight +
              changesContainerHeight)
        )
    );
  });

  const selectedBetType =
    storedBetType && BetUtils.getBetTypeFromRace(race, storedBetType);
  const selectedBetTypeCode = get(selectedBetType, "type.code");
  const isAccountCompliant =
    useSelector(isAccountCompliantSelector) &&
    isUserVerified &&
    userAcceptedTermsAndConditions;

  const [currentFPP] = enableFpp ? useFreePastPerformance(race) : [null];

  const [isStateBlocked, stateErrorMessage] = useBetBlockedByState();
  useScratchedRunners({ race });

  const { derbyRaceDate, derbyEventStatus, bannerCfg, onBannerCtaClick } =
    useDerbyBanner();

  const isResulted = race.results?.runners || race.results?.payoffs;

  const [tabSelection, setTabSelection] = useState<TabSelectionEnum>(
    TabSelectionEnum.RESULT
  );
  const {
    runnerModifier,
    runnerSortOrder,
    runnerSortBy,
    onRunnerModifierChangeHandler,
    onSortOrderChangeHandler,
    onSortByChangeHandler
  } = useRunnerModifier({
    isLogged,
    trackName: race.track?.trackName || "",
    raceNumber: race.number || ""
  });

  const isLegView = race.wagerable && (selectedBetType?.legCount || 0) > 1;
  const shouldBreakLegs = ((): boolean => {
    // leg counts breakpoints comming from figma
    if (!isLegView || selectedBetType?.legCount <= 5) {
      return false;
    }
    if (isDesktopMaxXl && !isDesktopMinXl) {
      return selectedBetType?.legCount > 5;
    }
    if (isDesktopMinXl && isDesktopMaxXxl && !isDesktopMinXxl) {
      return selectedBetType?.legCount > 7;
    }
    if (isDesktopMaxSize) {
      return selectedBetType?.legCount > 6;
    }
    if (isDesktopMinXxl) {
      return selectedBetType?.legCount > 5;
    }

    return true;
  })();

  const { pathname, search } = useLocation();

  const {
    isBetSlipVisible,
    onDelete: onDeleteSelections,
    onClickBetButton
  } = useSetBetSelections({
    selectedBetTypeCode,
    race,
    races,
    selectedBetType,
    columnCount: selectedBetType?.columnCount || 1
  });

  const dispatch = useDispatch();

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      dispatch(resetBetting());
    };
  }, []);

  useEffect(() => {
    setTabSelection(TabSelectionEnum.RESULT);
  }, [pathname, search]);

  useEffect(() => {
    if (requestBetWithAmount && userBalance) {
      if (+userBalance >= requestBetWithAmount) {
        setIsConfirmModalOpen(true);
        const scratches = BetUtils.setMatrixSizeByColumnCount(
          get(selectedBetType, "columnCount", 1)
        );
        dispatch(setSelectionScratched(scratches));
      }

      setRequesteBetWithAmount(undefined);
    }
  }, [requestBetWithAmount, userBalance]);

  const onPlaceBet = useCallback(
    (totalAmount: number) => {
      if (isLogged) {
        if ((isAccountCompliant && isFDR) || !isFDR) {
          setIsConfirmModalOpen(true);
          const scratches = BetUtils.setMatrixSizeByColumnCount(
            get(selectedBetType, "columnCount", 1)
          );
          dispatch(setSelectionScratched(scratches));
        } else {
          onOpenComplianceModal();
        }
      }
      if (!isLogged) {
        openLoginWall(() => setRequesteBetWithAmount(totalAmount));
      }
    },
    [isLogged, hasRequestedLogin, selectedBetType]
  );

  const handleRepeatWager = (raceWager: BetBasicInfo | BetRepeatInfo) => {
    setSelectedRaceWager(raceWager);
    setIsRepeatBetModalOpen(true);
  };

  const onClickActiveBetsButton = useCallback(() => {
    if (!isAccountCompliant && isFDR) {
      onOpenComplianceModal();
    } else {
      setIsBetsModalOpen(true);
    }
  }, [isAccountCompliant, isFDR]);

  const handleFPPDownload = (currentFpp: Record<string, string> | null) => {
    if (!isEmpty(getGeolocationErrorSelector)) {
      if (isTvg5()) {
        dispatch(openErrorModal());
      } else {
        dispatch(geoComplyErrorModal());
      }
      return;
    }

    if (currentFpp) {
      if (isLogged) {
        if (!isAccountCompliant && isFDR) {
          onOpenComplianceModal();
        } else {
          downloadUrl(currentFpp.link, currentFpp.filename, "pdf");
          mediator.base.dispatch({
            type: MediatorEventType.PROGRAM_FREE_PP_DOWNLOAD,
            payload: {
              raceNumber: get(race, "raceNumber", ""),
              trackName: get(race, "track.trackName", ""),
              raceType: get(race, "type.code", "")
            }
          });
        }
      } else {
        openLoginWall(() => {
          downloadUrl(currentFpp.link, currentFpp.filename, "pdf");
        });
      }
    }
  };

  const renderRunnerModifier = (
    currentRace: RaceProgram,
    isPicks: boolean = false,
    legIndex: number = 0
  ) => (
    <RunnerModifier
      isDesktopPicksFormat={isPicks && !isMobile}
      getRunnerModifier={onRunnerModifierChangeHandler}
      value={runnerModifier}
      sortOrder={runnerSortOrder}
      onSortOrderChange={onSortOrderChangeHandler}
      sortBy={runnerSortBy}
      onSortByChange={onSortByChangeHandler}
      betLeg={legIndex}
      currentRace={currentRace}
      selections={selections}
      isScrollableRaceCard={isLegView}
      betType={
        isPicks
          ? formattedScrollableWagerType(selectedBetType?.legCount)
          : selectedBetType
      }
    />
  );

  const renderRaceCard = () =>
    isLegView ? (
      <RaceCardContainer
        data-qa-label="race-card-container"
        ref={raceCardContainerRef}
      >
        <PicksSelector
          renderRunnerModifier={renderRunnerModifier}
          currentRace={race}
          races={races}
          isMobile={isMobile}
          runnerModifier={runnerModifier}
          runnerSortOrder={runnerSortOrder}
          runnerSortBy={runnerSortBy}
          isBetSlipVisible={isBetSlipVisible}
          onClickBetButton={onClickBetButton}
          onClickActiveBetsButton={() => setIsBetsModalOpen(true)}
          selectedBetType={selectedBetType}
          selections={selections}
          freePastPerformanceData={{
            currentFPP,
            onDownload: () => handleFPPDownload(currentFPP),
            title: currentFPP?.name || "",
            isMobile,
            fppSectionContent
          }}
        />
      </RaceCardContainer>
    ) : (
      <RaceCardContainer
        data-qa-label="race-card-container"
        ref={raceCardContainerRef}
      >
        {!isReplayModal && (
          <CurrentRaceDetails
            currentRace={race}
            onClickActiveBetsButton={onClickActiveBetsButton}
            onClickWatchReplay={onClickWatchReplay}
          />
        )}
        {!isResulted && renderRunnerModifier(race, false)}
        <>
          {race.results?.runners || race.results?.payoffs ? (
            <>
              <Divider />
              <RaceResultView
                currentRace={race}
                numRunnersAllRaces={races.map((r: RaceProgram) => ({
                  numRunners: r.numRunners
                }))}
                tabSelection={tabSelection}
                setTabSelection={setTabSelection}
                runnerModifier={runnerModifier}
                runnerSortOrder={runnerSortOrder}
                runnerSortBy={runnerSortBy}
                selectedBetType={selectedBetType}
                getRunnerModifier={onRunnerModifierChangeHandler}
                selections={selections}
                onSortOrderChange={onSortOrderChangeHandler}
                onSortByChange={onSortByChangeHandler}
              />
            </>
          ) : (
            <RunnerList
              currentRace={race}
              races={races}
              key={race.id || ""}
              onClickBetButton={onClickBetButton}
              selections={selections}
              runnerModifier={runnerModifier}
              runnerSortOrder={runnerSortOrder}
              runnerSortBy={runnerSortBy}
              isBetSlipVisible={isBetSlipVisible}
              numRunnersAllRaces={races.map((r: RaceProgram) => ({
                numRunners: r.numRunners
              }))}
            />
          )}
        </>
      </RaceCardContainer>
    );

  const renderVideoPlaylist = () =>
    isFDR && (
      <RaceVideoPlaylist
        currentRaceId={race.id}
        currentRaceDate={race.raceDate}
        qaLabel="race"
      />
    );

  return (
    <ApolloContext.Consumer>
      {(clients: ApolloContextClients) => (
        <>
          <PoolsNInfoModal
            isOpen={isPoolsNInfoModalOpen}
            subTitle={`${get(race, "track.trackName")} R${get(
              race,
              "raceNumber",
              ""
            )}`}
            onClose={() => dispatch(setIsPoolsNInfoModalOpen(false))}
            race={race}
            races={races}
          />
          <BetsModal
            isOpen={!myBetsIsOpen && isBetsModalOpen && isMobile}
            onClose={() => setIsBetsModalOpen(false)}
            race={race}
            races={races}
            onRepeatWager={handleRepeatWager}
          />
          <ConfirmBetModal
            race={race}
            races={races}
            selections={selections}
            isConfirmModalOpen={isConfirmModalOpen && !isLoading}
            setConfirmModalOpen={setIsConfirmModalOpen}
            behgClient={clients.behgClient}
            onRepeatWager={(wager: BetRepeatInfo) => {
              handleRepeatWager(wager);
              setIsConfirmModalOpen(false);
            }}
          />
          {selectedRaceWager && (
            <RepeatBetModal
              userBalance={userBalance}
              race={race}
              races={races}
              raceWager={selectedRaceWager!}
              isRepeatBetModalOpen={isRepeatBetModalOpen}
              setRepeatBetModalOpen={setIsRepeatBetModalOpen}
            />
          )}
          {isLoading || !race?.bettingInterests?.length ? (
            <MainContainer isMobile={isMobile} isReplayModal={isReplayModal}>
              <RaceCardLoading
                isMobile={isMobile}
                isLiveTv={isLiveTv}
                runnerModifier={runnerModifier}
              />
            </MainContainer>
          ) : (
            <>
              {isStateBlocked && (
                <AlertInlineContainer>
                  <AlertInline
                    type="fixed"
                    qaLabel="state-blocked-error"
                    message={stateErrorMessage}
                    variant="error"
                  />
                </AlertInlineContainer>
              )}
              <MainContainer
                data-qa-label="race-card-main-container"
                isReplayModal={isReplayModal}
              >
                <MainWrapper shouldBreakLegs={shouldBreakLegs}>
                  <PrimaryModulesContainer
                    data-qa-label="race-primary-modules-container"
                    shouldBreakLegs={shouldBreakLegs}
                    isPicksView={isLegView}
                    isReplayModal={isReplayModal}
                  >
                    {renderRaceCard()}
                    {renderVideoPlaylist()}
                    {!isMobile &&
                      !isReplayModal &&
                      programPageModuleConfig.probablesModule &&
                      !shouldBreakLegs && (
                        <Probables
                          ref={probablesRef}
                          currentRace={race}
                          key={race.id || ""}
                          nextRace={nextRace}
                        />
                      )}

                    {currentFPP && !isReplayModal && !isLegView && (
                      <FreePastPerformance
                        onDownload={() => handleFPPDownload(currentFPP)}
                        title={currentFPP.name}
                        isMobile={isMobile}
                        fppSectionContent={fppSectionContent}
                      />
                    )}
                  </PrimaryModulesContainer>
                  {!isMobile && !isReplayModal && (
                    <AuxiliaryModulesContainer
                      data-qa-label="race-auxiliary-modules-container"
                      shouldBreakLegs={shouldBreakLegs}
                    >
                      {derbyBannerImageToggle && bannerCfg && (
                        <RaceCardContainer>
                          <DerbyBanner
                            qaLabel="derbyBanner"
                            raceStartDate={derbyRaceDate}
                            eventStatus={derbyEventStatus}
                            bannerCfg={bannerCfg}
                            onCtaClick={onBannerCtaClick}
                          />
                        </RaceCardContainer>
                      )}
                      {programPageModuleConfig.activeBetsModule && (
                        <MyBetsCard
                          ref={myBetsContainerRef}
                          isLogged={isLogged}
                          race={race}
                          races={races}
                          behgClient={clients.behgClient}
                          onRepeatWager={handleRepeatWager}
                          isAuxiliaryHigherThanPrimary={
                            isAuxiliaryHigherThanPrimary
                          }
                          myBetsMaxHeight={myBetsMaxHeight}
                        />
                      )}
                      {programPageModuleConfig.poolsModule && (
                        <PoolsCard
                          ref={poolsContainerRef}
                          racePools={race.racePools}
                          bettingInterests={race.bettingInterests}
                          wagerTypeLimit={WAGER_TYPE_LIMIT}
                        />
                      )}
                      {programPageModuleConfig.willPaysModule && (
                        <WillPays
                          ref={willPaysContainerRef}
                          willPays={race.willPays}
                          key={race.id || ""}
                          bettingInterests={race.bettingInterests}
                          raceNumber={race.raceNumber}
                          trackName={race.track?.trackName}
                        />
                      )}
                      {programPageModuleConfig.changesModule && isLogged ? (
                        <ChangesCard
                          ref={changesContainerRef}
                          changes={race.changes}
                          bettingInterests={race.bettingInterests}
                          raceTypeCode={race.type?.code || RaceTypeCodeEnum.ALL}
                        />
                      ) : null}
                    </AuxiliaryModulesContainer>
                  )}
                </MainWrapper>
                <BetSlip
                  betType={selectedBetTypeCode}
                  isVisible={isBetSlipVisible}
                  isLoading={isLogged && userBalance === undefined}
                  race={race}
                  races={races}
                  onDelete={onDeleteSelections}
                  selections={selections}
                  onPlaceBet={onPlaceBet}
                  isBettingDisabled={isStateBlocked}
                />
              </MainContainer>
            </>
          )}
        </>
      )}
    </ApolloContext.Consumer>
  );
};

export default Main;
